
import { supabase } from '../lib/supabase';

const levelNumber = value => Number(String(value).match(/[3-7]/)?.[0]);
const syncAdmissionLevels = async program => {
    const levels = (program.levels || []).map(levelNumber).filter(Boolean);
    if (!levels.length) return;
    const capacities = program.level_capacities || program.levelCapacities || {};
    const rows = levels.map(level => ({
        program_id: String(program.id), level,
        program_level_code: program.levelCodes?.[level] || `${String(program.code || 'PROGRAM').replace(/[^a-zA-Z0-9]/g, '').toUpperCase()}L${level}`,
        capacity: Number(capacities[level]) || 0,
        self_sponsored_capacity: Number(capacities[level]) || 0,
        kuccps_capacity: 0,
        is_active: program.is_active !== false && String(program.status || '').toLowerCase() !== 'inactive'
    }));
    // Keep each level's configured code and capacity synchronized when a
    // programme is edited; ignoring duplicates leaves stale codes in place.
    const { error } = await supabase.from('program_admission_levels').upsert(rows, { onConflict:'program_id,level' });
    if (error) throw error;
};

const mockPrograms = [
    { id: 'prog_001', code: 'BCS', name: 'Bachelor of Computer Science', duration: '4 years', department: 'Computing' },
    { id: 'prog_002', code: 'BIT', name: 'Bachelor of Information Technology', duration: '3 years', department: 'Computing' },
];

export const programService = {
    getAll: async () => {
        // Fetch programs
        const { data: scopedRows, error } = await supabase.rpc('get_scoped_programs');

        if (error) throw error;
        const data = (scopedRows || []).map(row => row.program || row).sort((a,b) => String(a.name || '').localeCompare(String(b.name || '')));

        // Fetch student counts
        const { data: students, error: studentError } = await supabase
            .from('students')
            .select('profile_id,program_id,status,enrollment_status');
        if (studentError) console.warn('Programme student mappings could not be loaded:', studentError.message);

        const { data: semesterEnrollments, error: enrollmentError } = await supabase
            .from('student_semester_enrollments')
            .select('student_id,program_id,status')
            .in('status', ['reported','active','registration_completed','billed']);
        if (enrollmentError) console.warn('Semester enrollment totals could not be loaded:', enrollmentError.message);

        // Fetch unit data for counting
        const { data: units, error: unitError } = await supabase
            .from('units')
            .select('program_id, unit_type, module');

        if (unitError) {
            console.error('ProgramService: Error fetching units:', unitError);
        }

        // Group student counts
        const studentsByProgram = {};
        const addStudent = (programId, studentId) => {
            if (!programId || !studentId) return;
            if (!studentsByProgram[programId]) studentsByProgram[programId] = new Set();
            studentsByProgram[programId].add(String(studentId));
        };
        (students || []).forEach(student => {
            const status = String(student.status || student.enrollment_status || '').toLowerCase();
            if (['active','admitted','enrolled','reported','registration_completed','billed'].includes(status)) addStudent(student.program_id, student.profile_id);
        });
        (semesterEnrollments || []).forEach(enrollment => addStudent(enrollment.program_id, enrollment.student_id));
        const studentCounts = Object.fromEntries(Object.entries(studentsByProgram).map(([programId, ids]) => [programId, ids.size]));

        // Map back to camelCase for UI and calculate capacity
        return data.map(p => {
            // Calculate capacity from level capacities if available
            let calculatedCapacity = 0;
            const caps = p.level_capacities || {};
            calculatedCapacity = Object.values(caps).reduce((sum, val) => sum + (parseInt(val) || 0), 0);

            // Filter units for this specific program
            const pUnits = units?.filter(u => u.program_id === p.id) || [];

            // Calculate level-based unit counts (cumulative: Module 1 up to level's duration)
            const levelUnitCounts = (p.levels || []).map(level => {
                const levelNumMatch = String(level).match(/\d+/);
                const levelNum = levelNumMatch ? levelNumMatch[0] : null;
                const maxMod = parseInt(p.level_durations?.[levelNum] || 0);

                // Count all units in modules 1 to maxMod
                const count = pUnits.filter(u => (u.module || 1) <= maxMod).length;

                const labelMap = {
                    'level_3': 'Level 3',
                    'level_4_artisan': 'Level 4',
                    'level_5_craft': 'Level 5',
                    'level_6_diploma': 'Level 6'
                };

                return {
                    level,
                    label: labelMap[level] || level,
                    count
                };
            });

            return {
                ...p,
                departmentId: p.department_id,
                isActive: p.is_active,
                levelDurations: p.level_durations || {},
                levelCapacities: p.level_capacities || {},
                tuitionFee: p.tuition_fee || 0,
                levelTuitionFees: p.level_tuition_fees || {},
                capacity: calculatedCapacity || p.capacity || 0,
                enrolledStudents: studentCounts[p.id] || 0,
                totalUnits: pUnits.length,
                levelUnitCounts: levelUnitCounts
            };
        });
        stone - age - count - fix
    },

    /**
     * Return the institution's programme classes (intakes) with the number of
     * active enrolments attached to each class.  Programme totals are useful
     * for administration screens, but they are ambiguous when a programme
     * runs more than one intake, so executive reporting uses this method to
     * keep the intake boundary explicit.
     */
    getIntakeFootprint: async () => {
        const { data: classes, error: classesError } = await supabase
            .from('program_classes')
            .select('id, program_id, intake_name, intake_code, start_date, end_date, is_active, current_enrollment')
            .order('start_date', { ascending: false });

        if (classesError) throw classesError;

        const { data: enrollments, error: enrollmentsError } = await supabase
            .from('student_enrollments')
            .select('student_id, program_id, program_class_id, status')
            .eq('status', 'active');

        // Some installations have the programme-class table but restrict the
        // enrolment rows.  The class's maintained current_enrollment value is
        // a safe fallback in that case, while a successful query (including an
        // empty result) remains the source of truth.
        if (enrollmentsError) {
            console.warn('Intake enrolment totals could not be loaded:', enrollmentsError.message);
        }

        const studentsByClass = {};
        (enrollments || []).forEach((enrollment) => {
            if (!enrollment?.program_class_id || !enrollment?.student_id) return;
            if (!studentsByClass[enrollment.program_class_id]) {
                studentsByClass[enrollment.program_class_id] = new Set();
            }
            studentsByClass[enrollment.program_class_id].add(String(enrollment.student_id));
        });

        return (classes || []).map((programClass) => ({
            ...programClass,
            studentCount: enrollmentsError || !Object.prototype.hasOwnProperty.call(studentsByClass, programClass.id)
                ? Number(programClass.current_enrollment || 0)
                : studentsByClass[programClass.id].size,
        }));
    },

    delete: async (id) => {
        const { error } = await supabase
            .from('programs')
            .delete()
            .eq('id', id);

        if (error) throw error;
        return true;
    },

    getPrograms: async () => {
        const { data: scopedRows, error } = await supabase.rpc('get_scoped_programs');

        if (error) throw error;
        return (scopedRows || []).map(row => row.program || row).sort((a,b) => String(a.name || '').localeCompare(String(b.name || '')));
    },

    getById: async (id) => {
        const { data: scopedRows, error } = await supabase.rpc('get_scoped_programs');
        if (error) throw error;
        const data = (scopedRows || []).map(row => row.program || row).find(row => String(row.id) === String(id));
        if (!data) throw new Error('Programme is outside your organisational scope.');
        // Calculate capacity
        let calculatedCapacity = 0;
        const caps = data.level_capacities || {};
        calculatedCapacity = Object.values(caps).reduce((sum, val) => sum + (parseInt(val) || 0), 0);

        // Fetch students to calculate breakdown
        const { data: students } = await supabase
            .from('students')
            .select('academic_level, status')
            .eq('program_id', id)
            .in('status', ['Active', 'Admitted', 'active', 'admitted', 'ACTIVE', 'ADMITTED']);

        // Fetch units for this program
        const { data: units } = await supabase
            .from('units')
            .select('*')
            .eq('program_id', id);

        // Group units by module for Breakdown view
        const unitsByLevel = {};
        if (units) {
            units.forEach(u => {
                const mod = u.module || '1';
                if (!unitsByLevel[mod]) unitsByLevel[mod] = [];
                unitsByLevel[mod].push(u);
            });
        }

        const enrolledByLevel = {};
        if (students) {
            students.forEach(s => {
                // Match various potential level formats (keys, labels, short names)
                const levelKey = s.academic_level;
                if (!levelKey) return;

                const mapping = {
                    'Level 3 (NVC)': ['level_3', '3', 'Level 3 (NVC)'],
                    'Level 4 (Artisan)': ['level_4_artisan', '4', 'Level 4 (Artisan)'],
                    'Level 5 (Craft)': ['level_5_craft', '5', 'Level 5 (Craft)'],
                    'Level 6 (Diploma)': ['level_6_diploma', '6', 'Level 6 (Diploma)'],
                    'level_3': ['level_3', '3', 'Level 3 (NVC)'],
                    'level_4_artisan': ['level_4_artisan', '4', 'Level 4 (Artisan)'],
                    'level_5_craft': ['level_5_craft', '5', 'Level 5 (Craft)'],
                    'level_6_diploma': ['level_6_diploma', '6', 'Level 6 (Diploma)']
                };

                const keys = mapping[levelKey] || [levelKey];
                keys.forEach(k => {
                    enrolledByLevel[k] = (enrolledByLevel[k] || 0) + 1;
                });
            });
        }

        return {
            ...data,
            departmentId: data.department_id,
            isActive: data.is_active,
            levelDurations: data.level_durations || {},
            levelCapacities: data.level_capacities || {},
            tuitionFee: data.tuition_fee || 0,
            levelTuitionFees: data.level_tuition_fees || {},
            capacity: calculatedCapacity || data.capacity || 0,
            enrolledStudents: students?.length || 0,
            enrolledByLevel: enrolledByLevel,
            units: units || [],
            totalUnits: units?.length || 0,
            unitsByLevel: unitsByLevel
        };
    },

    update: async (id, data) => {
        const dbData = { ...data };
        const admissionLevelCodes = dbData.levelCodes || {};
        delete dbData.levelCodes;

        if (dbData.departmentId) {
            dbData.department_id = dbData.departmentId;
            delete dbData.departmentId;
        }
        if (typeof dbData.tuitionFee !== 'undefined') {
            dbData.tuition_fee = dbData.tuitionFee;
            delete dbData.tuitionFee;
        }
        if (typeof dbData.levelTuitionFees !== 'undefined') {
            dbData.level_tuition_fees = dbData.levelTuitionFees;
            delete dbData.levelTuitionFees;
        }
        if (dbData.levelDurations) {
            dbData.level_durations = dbData.levelDurations;
            delete dbData.levelDurations;
        }
        if (dbData.levelCapacities) {
            dbData.level_capacities = dbData.levelCapacities;
            delete dbData.levelCapacities;
        }
        if (typeof dbData.isActive !== 'undefined') {
            dbData.is_active = dbData.isActive;
            delete dbData.isActive;
        }
        if (typeof dbData.capacity !== 'undefined') {
            delete dbData.capacity;
        }
        if (dbData.levels && dbData.levels.length > 0) {
            dbData.level = dbData.levels[0];
        }
        if (!dbData.duration_months && dbData.level_durations) {
            const firstDuration = Object.values(dbData.level_durations)[0];
            if (firstDuration) {
                const num = parseInt(firstDuration);
                if (!isNaN(num)) {
                    if (firstDuration.toLowerCase().includes('year')) {
                        dbData.duration_months = num * 12;
                    } else {
                        dbData.duration_months = num;
                    }
                } else {
                    dbData.duration_months = 12;
                }
            } else {
                dbData.duration_months = 12;
            }
        }

        const { data: updated, error } = await supabase
            .from('programs')
            .update(dbData)
            .eq('id', id)
            .select()
            .single();

        if (error) throw error;
        await syncAdmissionLevels({ ...updated, levelCodes:admissionLevelCodes });
        return updated;
    },

    create: async (data) => {
        const { id, ...insertData } = data;
        const admissionLevelCodes = insertData.levelCodes || {};
        delete insertData.levelCodes;

        if (insertData.departmentId) {
            insertData.department_id = insertData.departmentId;
            delete insertData.departmentId;
        }
        if (typeof insertData.tuitionFee !== 'undefined') {
            insertData.tuition_fee = insertData.tuitionFee;
            delete insertData.tuitionFee;
        }
        if (typeof insertData.levelTuitionFees !== 'undefined') {
            insertData.level_tuition_fees = insertData.levelTuitionFees;
            delete insertData.levelTuitionFees;
        }
        if (insertData.levelDurations) {
            insertData.level_durations = insertData.levelDurations;
            delete insertData.levelDurations;
        }
        if (insertData.levelCapacities) {
            insertData.level_capacities = insertData.levelCapacities;
            delete insertData.levelCapacities;
        }
        if (typeof insertData.isActive !== 'undefined') {
            insertData.is_active = insertData.isActive;
            delete insertData.isActive;
        }
        if (typeof insertData.capacity !== 'undefined') {
            delete insertData.capacity;
        }
        if (insertData.levels && insertData.levels.length > 0 && !insertData.level) {
            insertData.level = insertData.levels[0];
        }
        if (!insertData.duration_months && insertData.level_durations) {
            const firstDuration = Object.values(insertData.level_durations)[0];
            if (firstDuration) {
                const num = parseInt(firstDuration);
                if (!isNaN(num)) {
                    if (firstDuration.toLowerCase().includes('year')) {
                        insertData.duration_months = num * 12;
                    } else {
                        insertData.duration_months = num;
                    }
                } else {
                    insertData.duration_months = 12;
                }
            } else {
                insertData.duration_months = 12;
            }
        }

        const { data: created, error } = await supabase
            .from('programs')
            .insert([insertData])
            .select()
            .single();

        if (error) throw error;
        await syncAdmissionLevels({ ...created, levelCodes:admissionLevelCodes });
        return created;
    },

    getProgramLevels: async (programId) => {
        const { data, error } = await supabase
            .from('programs')
            .select('levels')
            .eq('id', programId)
            .single();

        if (error) throw error;
        return data?.levels || [];
    },

    getAcademicYears: async () => {
        const { data, error } = await supabase.from('academic_years').select('*').order('start_date', { ascending:false });
        if (error) throw error;
        return (data || []).map(item => ({ ...item, year_name:item.name, current:item.status === 'active', is_current:item.status === 'active' }));
    },

    getSemesters: async () => {
        const { data, error } = await supabase.from('semesters').select('*').order('start_date', { ascending:false });
        if (error) throw error;
        return (data || []).map(item => ({ ...item, semester_name:item.name, current:item.status === 'active', is_current:item.status === 'active' }));
    },

    getStats: async () => {
        try {
            const { data: programs, error: progError } = await supabase
                .from('programs')
                .select('id, is_active, department_id');

            if (progError) throw progError;
            const activePrograms = (programs || []).filter(program => program.is_active !== false);
            const activeDepartmentIds = new Set(activePrograms.map(program => program.department_id).filter(Boolean));

            return {
                activePrograms: activePrograms.length,
                departments: activeDepartmentIds.size
            };
        } catch (error) {
            console.error('Error fetching program stats:', error);
            throw error;
        }
    }
};

// Named exports retained for screens that import the service module as a namespace.
export const getPrograms = (...args) => programService.getPrograms(...args);
export const getProgramLevels = (...args) => programService.getProgramLevels(...args);
export const getAcademicYears = (...args) => programService.getAcademicYears(...args);
export const getSemesters = (...args) => programService.getSemesters(...args);
